import React from 'react';
import {
  StatusPill,
  Avatar,
  AvatarImage,
  AvatarFallback,
  CardActionsMenu,
} from '@/shared/components/ui';
import type { CardAction } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { getProjectStatusPill, fmtShortDate } from './ProjectCard';
import type { ProjectListItem } from '../types';

interface UserLite {
  id: string;
  name: string;
  avatar?: string;
}

/** Avatar compacto do líder (fallback = inicial em slate). */
const LeaderAvatar: React.FC<{ user?: UserLite }> = ({ user }) => (
  <span title={user?.name}>
    <Avatar className="w-7 h-7" isSquircle>
      {user?.avatar && <AvatarImage src={user.avatar} alt={user.name} isSquircle />}
      <AvatarFallback isSquircle>
        <span className="text-white font-semibold" style={{ fontSize: 11 }}>
          {(user?.name ?? '?').charAt(0).toUpperCase()}
        </span>
      </AvatarFallback>
    </Avatar>
  </span>
);

/** Barra de progresso da linha. */
const ProgressCell: React.FC<{ project: ProjectListItem }> = ({ project }) => {
  const done = project.progress?.done ?? 0;
  const total = project.progress?.total ?? 0;
  const pct = Math.max(0, Math.min(100, project.progress?.pct ?? 0));
  return (
    <div style={{ maxWidth: 170 }}>
      <div
        className="h-1.5 rounded-full overflow-hidden mb-1"
        style={{ backgroundColor: 'var(--theme-border)' }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: pct === 100 ? 'var(--theme-success-color)' : 'var(--theme-primary)' }}
        />
      </div>
      <span className="tabular-nums" style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}>
        {done}/{total} · {pct}%
      </span>
    </div>
  );
};

export interface ProjectListRowProps {
  /** Item de listagem. */
  project: ProjectListItem;
  /** Nome do pipeline resolvido (via `usePipelinesDropdown` na página). */
  pipelineName?: string;
  /** Dados leves do líder (via `useUsersDropdown` na página). */
  leader?: UserLite;
  /** Linha selecionada (bulk). */
  selected?: boolean;
  /** Alterna a seleção da linha. */
  onToggleSelect?: (id: string) => void;
  /** Abre o workspace do projeto. */
  onOpen: () => void;
  /** Arquiva (status `active/completed`) ou restaura (status `archived`). */
  onArchiveToggle?: (id: string, status: ProjectListItem['status']) => void;
  /** Exclui o projeto. */
  onDelete?: (id: string) => void;
}

/**
 * Linha de tabela de projeto (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + coluna multi-linha (nome/cliente + contadores)
 * + menu ••• de ações (última col). Espelha as colunas do mockup `projetos.html`.
 */
export const ProjectListRow: React.FC<ProjectListRowProps> = ({
  project,
  pipelineName,
  leader,
  selected = false,
  onToggleSelect,
  onOpen,
  onArchiveToggle,
  onDelete,
}) => {
  const isActive = project.status === 'active';
  const isArchived = project.status === 'archived';
  const pill = getProjectStatusPill(project.status);

  const actions: CardAction[] = [
    { label: 'Abrir workspace', icon: 'arrow-right', onClick: onOpen },
  ];
  if (onArchiveToggle) {
    actions.push({
      label: isArchived ? 'Restaurar' : 'Arquivar',
      icon: 'archive',
      onClick: () => onArchiveToggle(project.id, project.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir projeto', icon: 'trash', onClick: () => onDelete(project.id), variant: 'danger' });
  }

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{
        borderColor: 'var(--theme-card-border)',
        backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
        ...(isActive ? {} : { opacity: 0.68 }),
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      onClick={onOpen}
      data-testid={`projects-row-${project.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => onToggleSelect?.(project.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`projects-check-${project.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Projeto */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: project.color }}
          />
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {project.name}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {project.clientName ? `${project.clientName} · ` : ''}{project.taskCount} tarefas · {project.milestoneCount} marcos
            </div>
          </div>
        </div>
      </td>

      {/* Pipeline */}
      <td className="px-4 py-3">
        {pipelineName ? (
          <span
            className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
          >
            <Icon name="trending-up" className="w-3 h-3" />
            <span className="truncate" style={{ maxWidth: 140 }}>{pipelineName}</span>
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Líder */}
      <td className="px-4 py-3">
        <LeaderAvatar user={leader} />
      </td>

      {/* Progresso */}
      <td className="px-4 py-3">
        <ProgressCell project={project} />
      </td>

      {/* Período */}
      <td className="px-4 py-3">
        <span className="whitespace-nowrap tabular-nums" style={{ fontSize: 12, color: 'var(--theme-muted-text)' }}>
          {fmtShortDate(project.startDate)} – {fmtShortDate(project.endDate)}
        </span>
      </td>

      {/* Próximo marco */}
      <td className="px-4 py-3">
        {project.overdueCount > 0 ? (
          <span className="inline-flex items-center gap-1" style={{ fontSize: 12, color: 'var(--theme-error-fg)', fontWeight: 600 }}>
            <Icon name="alert-triangle" className="w-3.5 h-3.5" />
            {project.overdueCount} tarefa{project.overdueCount > 1 ? 's' : ''} atrasada{project.overdueCount > 1 ? 's' : ''}
          </span>
        ) : project.nextMilestone ? (
          <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--theme-card-text)' }}>
            <Icon name="flag" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
            {project.nextMilestone.title}
            <span style={{ color: 'var(--theme-muted-text)' }}>· {fmtShortDate(project.nextMilestone.date)}</span>
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Status — F62/F65/F66: StatusPill, 'Concluído' verde, dot default */}
      <td className="px-4 py-3">
        <StatusPill variant={pill.variant} label={pill.label} data-testid={`projects-row-status-${project.id}`} />
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" />
      </td>
    </tr>
  );
};

ProjectListRow.displayName = 'ProjectListRow';
